import React, { useState, useRef, useEffect, startTransition } from 'react';
import { Globe, Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LOCALES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'it', label: 'Italian', native: 'Italiano' },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
  { code: 'ru', label: 'Russian', native: 'Русский' },
  { code: 'ja', label: 'Japanese', native: '日本語' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'ko', label: 'Korean', native: '한국어' },
  { code: 'tr', label: 'Turkish', native: 'Türkçe' },
];

const LocaleSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Normalize language code (e.g. "en-US" → "en")
  const activeLng = (i18n.language || 'en').split('-')[0];
  const currentLocale = LOCALES.find(l => l.code === activeLng) || LOCALES[0];

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (code: string) => {
    if (code === activeLng) {
      setOpen(false);
      return;
    }
    setLoading(true);
    setOpen(false);
    startTransition(() => {
      i18n.changeLanguage(code).finally(() => setLoading(false));
    });
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-n-sm text-[11px] font-semibold text-n-text-md hover:text-n-text hover:bg-n-hover transition-colors"
        title={t('locale_switcher_title', 'Language: {{lang}}', { lang: currentLocale.label })}
        aria-label={t('locale_switcher_aria', 'Switch language')}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={loading}
      >
        {loading ? (
          <Loader2 size={14} className="text-n-text-lt animate-spin" />
        ) : (
          <Globe size={14} className="text-n-text-lt" />
        )}
        <span className="font-mono text-[10px] font-bold tracking-wide">{currentLocale.code.toUpperCase()}</span>
      </button>

      {open && (
        <div
          className="n-popover right-0 top-full mt-1.5 w-52 py-1 z-[9999]"
          role="listbox"
          aria-label={t('locale_switcher_select', 'Select language')}
        >
          <div className="px-3 py-2 border-b border-n-border">
            <p className="n-label">{t('locale_switcher_heading', 'App Language')}</p>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {LOCALES.map(locale => (
              <button
                key={locale.code}
                role="option"
                aria-selected={locale.code === currentLocale.code}
                onClick={() => handleSelect(locale.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-[13px] hover:bg-n-hover transition-colors ${
                  locale.code === currentLocale.code ? 'text-n-blue font-medium bg-n-blue-bg/50' : 'text-n-text'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <span className="font-mono text-[10px] font-bold text-n-text-lt w-6 shrink-0">{locale.code.toUpperCase()}</span>
                  <div className="text-left">
                    <div className="leading-tight">{locale.native}</div>
                    <div className="text-[10px] text-n-text-lt leading-tight">{locale.label}</div>
                  </div>
                </div>
                {locale.code === currentLocale.code && <Check size={14} className="text-n-blue shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocaleSwitcher;
